import { useState, useEffect, useMemo } from 'react';
import * as R from 'ramda';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { ContainedAxiosManager } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/AxiosClient';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useCardHeaders } from '../../../transaction/src/contexts/Parameters/hooks';
import { PaymentCardIdentification } from '../types';
import { MAX_REQUESTS, MAX_TIMEOUT_MINUTES } from './constants';


export enum TopUpSSEStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  DECLINED = 'DECLINED',
}


export type TopUpSSEMessageResponseBody = {
  instructionIdentifications: PaymentCardIdentification[];
  status: {
    status: TopUpSSEStatus;
    statusReasonCode: string;
    statusReasonDetails: string;
  };
};

export type TopUpSSEMessageResponse = {
  headers: [];
  body: TopUpSSEMessageResponseBody;
  sessionId: string;
  messageId: string;
  comment: Nullable<string>;
  eventType: string;
};

const useSSETopUpStatus = (
  instructionIdentifier: Nullable<string>,
  processId: Nullable<string>,
  shouldInitiateSSE: boolean,
  onClose?: (msg?: string) => void,
  minutesToTimeout: number = MAX_TIMEOUT_MINUTES,
  maximumNumberOfRequests: number = MAX_REQUESTS,
) => {
  const [data, setData] = useState<Nullable<TopUpSSEMessageResponseBody>>(null);
  const [error, setError] = useState<Nullable<string>>(null);
  const [controller, setController] = useState<AbortController | null>(null);
  const [numberOfRequests, setNumberOfRequests] = useState(0);
  const [isTimeoutExceeded, setIsTimeoutExceeded] = useState(false);
  const cardHeaders = useCardHeaders();
  const headersForCards = useMemo(() => ({ ...cardHeaders, Platform: 'web' }), [cardHeaders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTimeoutExceeded(true);
    }, minutesToTimeout);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const axiosInstance = new ContainedAxiosManager().getInstance();
    const headers = R.pick(
      ['Authorization', 'Brand', 'Channel', 'Tenant', 'User-Agent-Type'],
      axiosInstance.defaults.headers.common,
    );
    const ctrl = new AbortController();
    setController(ctrl);
    const fetchData = async () => {
      await fetchEventSource(
        `/secure-web/dx4b/payment-card/sse/connect/top-up-update-status/${instructionIdentifier}/${processId}`,
        {
          method: 'GET',
          headers: {
            ...headers,
            ...headersForCards,
          },
          signal: ctrl.signal,
          onmessage(event) {
            setData((JSON.parse(event.data) as TopUpSSEMessageResponse).body);
          },
          onerror(err) {
            setError(err?.message);
          },
          onclose() {
            setNumberOfRequests(prev => prev + 1);
          },
        },
      );
    };

    if (instructionIdentifier && processId && shouldInitiateSSE) {
      if (!isTimeoutExceeded) {
        fetchData().then();
      } else {
        controller?.abort();
        onClose && onClose();
      }
    }
  }, [instructionIdentifier, processId, headersForCards, shouldInitiateSSE, numberOfRequests, isTimeoutExceeded]);

  return {
    data,
    error,
    setError,
    controller,
  };
};

export { useSSETopUpStatus };
